import React from 'react';
import MainLayout from '../layouts/MainLayout';
import SEOHead from '../components/SEOHead';
import { MEDIA_ASSETS } from '../utils/mediaAssets';

const AboutUsPage = () => {
  return (
    <MainLayout>
      <SEOHead 
        title="За нас - Refresh Bulgaria"
        description="Научете повече за Refresh Bulgaria - доброволческо движение за опазване на околната среда в България. Нашата мисия, ценности и визия за по-зелено бъдеще."
        keywords="за нас refresh bulgaria, мисия екология, доброволчество България, опазване околна среда, устойчивост"
        url="https://refreshbulgaria.com/about"
      />
      <div className="max-w-screen-xl mx-auto px-4">
        <main className="mt-4">
          {/* Hero Section */}
          <section className="mb-8">
            <div className="relative w-full h-64 md:h-80 rounded-lg overflow-hidden mb-6">
              <img 
                src={MEDIA_ASSETS.aboutUsHeroImage} 
                alt="За нас - Refresh Bulgaria движение за опазване на околната среда в България" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <div className="text-center text-white p-6">
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                    За <span className="text-green-300">нас</span>
                  </h1>
                  <p className="text-base md:text-xl font-semibold opacity-90 max-w-2xl">
                    RefreshBulgaria - движение за по-чиста и зелена България
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Video and Mission Section */}
          <section className="mb-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex flex-col lg:flex-row gap-8 items-start">
                <div className="lg:w-1/2">
                  <div className="rounded-lg aspect-[3/4] overflow-hidden shadow-lg">
                    <video 
                      src={MEDIA_ASSETS.aboutUsVideo}
                      controls
                      className="w-full h-full object-cover"
                      poster=""
                    >
                      Вашият браузър не поддържа видео елементи.
                    </video>
                  </div>
                </div>
                <div className="lg:w-1/2 flex flex-col justify-center">
                  <div className="space-y-8 px-4 lg:px-8">
                    {/* Our Mission Section */}
                    <div>
                      <h2 className="text-2xl font-bold text-green-800 mb-4">Нашата мисия</h2>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        RefreshBulgaria е доброволческо движение, което обединява граждани в общата цел за 
                        по-чиста и зелена България. Вярваме, че всеки от нас има силата да направи промяна 
                        и че заедно можем да създадем по-устойчиво бъдеще за нашата страна.
                      </p>
                      <p className="text-gray-700 leading-relaxed">
                        Нашата мисия е да мобилизираме граждани от цяла България в конкретни действия за 
                        опазване на околната среда, като започнем с почистването на замърсени природни 
                        райони и градски пространства. Стремим се да повишим екологичното съзнание и да 
                        създадем култура на отговорност към природата.
                      </p>
                    </div>

                    {/* Our Future Section */}
                    <div>
                      <h2 className="text-2xl font-bold text-green-800 mb-4">Нашето бъдеще</h2>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        Виждаме RefreshBulgaria като национално движение, което не само почиства, но и 
                        предотвратява замърсяването чрез образователни програми и партньорства с местни 
                        общности, училища и бизнеса.
                      </p>
                      <p className="text-gray-700 leading-relaxed">
                        В дългосрочен план планираме да разширим дейността си с програми за рециклиране, 
                        залесяване, опазване на биоразнообразието и насърчаване на устойчиви практики 
                        в ежедневието. Нашата цел е България да стане пример за екологично съзнание 
                        и грижа за природата в Европа.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Values Section */}
          <section className="mb-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-2xl font-bold text-green-800 mb-4">Нашите ценности</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-green-800 mb-3">🌱 Устойчивост</h3>
                  <p className="text-gray-600 mb-4">Вярваме в дългосрочни решения, които не само почистват, но и предотвратяват бъдещо замърсяване.</p>
                  
                  <h3 className="text-lg font-semibold text-green-800 mb-3">🤝 Сътрудничество</h3>
                  <p className="text-gray-600">Работим заедно с общини, училища, бизнеса и граждани за максимално въздействие.</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-green-800 mb-3">💚 Отговорност</h3>
                  <p className="text-gray-600 mb-4">Всеки от нас носи отговорност за опазването на природата за бъдещите поколения.</p>
                  
                  <h3 className="text-lg font-semibold text-green-800 mb-3">🌟 Вдъхновение</h3>
                  <p className="text-gray-600">Целим да вдъхновим позитивна промяна в нагласите и поведението към околната среда.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Call to Action */}
          <section className="mb-8">
            <div className="bg-gradient-to-r from-green-700 to-green-600 p-8 rounded-lg text-white text-center">
              <h2 className="text-2xl font-bold mb-4">Присъединете се към нас!</h2>
              <p className="text-lg mb-6 opacity-90">
                Заедно можем да направим България по-чиста и красива. Всяко действие има значение.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="/events" 
                  className="bg-white hover:bg-green-50 text-green-700 font-semibold py-3 px-6 rounded-lg transition-colors text-center"
                >
                  Вижте събитията ни
                </a>
                <a 
                  href="/get-involved" 
                  className="bg-green-800 hover:bg-green-900 text-white border border-green-800 font-semibold py-3 px-6 rounded-lg transition-colors text-center"
                >
                  Включете се
                </a>
              </div>
            </div>
          </section>
        </main>
      </div>
    </MainLayout>
  );
};

export default AboutUsPage; 